import { memo, useCallback, useState, useEffect, useRef } from 'react'
import { login } from 'services/Authentication'
import styled from 'styled-components'

import { hp } from 'styles/Sizes'
import { primary, textPrimary, error as errorColor } from 'styles/Colors'
import Typography from 'components/common/Typography'
import DefaultBtn from 'components/common/buttons/Default'
import useTranslation from 'services/Translation/useTranslation'
import Separator from 'components/common/Separator'

const OTP_LENGTH = 5

const Wrapper = styled.div`
  font-family: 'coresanscr45', sans-serif;
`

const OtpContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`

const OtpInput = styled.input<{ hasValue: boolean; hasError: boolean }>`
  width: 52px;
  height: 58px;
  text-align: center;
  font-size: 28px;
  font-family: 'coresanscr45', sans-serif;
  border-radius: 8px;
  border: 2px solid ${({ hasError, hasValue }) =>
    hasError ? errorColor : hasValue ? primary : textPrimary};
  background-color: #fff;
  color: ${textPrimary};
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${primary};
  }

  /* Hide number spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    title: getText('Two factor authentication', 'Auth_Login_2FA_Form'),
    subtitle1: getText('We sent a code to ', 'Auth_Login_2FA_Form'),
    subtitle2: getText('. Please enter it below.', 'Auth_Login_2FA_Form'),
    errorReq: getText('Code is required.', 'Auth_Login_2FA_Form'),
    errorTypeLength: getText('Code must be 5 numbers', 'Auth_Login_2FA_Form'),
    btnLabel: getText('Continue', 'Auth_Login_2FA_Form'),
    didntReceive: getText("Didn't receive the code?", 'Auth_Login_2FA_Form'),
    resendBtn: getText('Resend Code', 'Auth_Login_2FA_Form'),
    resendSuccess: getText('Code sent successfully', 'Auth_Login_2FA_Form'),
    resendError: getText('Failed to send code', 'Auth_Login_2FA_Form'),
    resendCooldown: getText('Please wait {seconds}s before resending', 'Auth_Login_2FA_Form'),
    sending: getText('Sending...', 'Auth_Login_2FA_Form'),
  }[key])

interface CodeFormProps {
  onSubmit: (code: string) => Promise<void>
  partialTarget: string
  error: string
  loading: boolean
  loginCredentials: {
    email: string
    password: string
  } | null
}

const CodeForm = ({ onSubmit, partialTarget, error: propsError, loading, loginCredentials }: CodeFormProps) => {
  const { t } = useTranslation(texts)
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    let timer: any
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(c => c - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const code = digits.join('')

  const handleChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const char = value.slice(-1)
    setDigits(prev => {
      const next = [...prev]
      next[index] = char
      return next
    })
    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }, [])

  const handleKeyDown = useCallback((index: number, e: any) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }, [digits])

  const handlePaste = useCallback((e: any) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    setDigits(prev => {
      const next = [...prev]
      for (let i = 0; i < OTP_LENGTH; i++) {
        next[i] = pasted[i] || ''
      }
      return next
    })
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusIdx]?.focus()
  }, [])

  const handleSubmit = useCallback(async () => {
    if (code.length === OTP_LENGTH) {
      await onSubmit(code)
    }
  }, [code, onSubmit])

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || !loginCredentials) return
    setResendLoading(true)
    setResendMessage('')
    try {
      const res = await login({
        email: loginCredentials.email,
        password: loginCredentials.password,
        language_tag: 'en',
      })
      if (res?.error) {
        setResendMessage(t('resendError'))
      } else {
        setResendMessage(t('resendSuccess'))
        setResendCooldown(30)
      }
    } catch {
      setResendMessage(t('resendError'))
    }
    setResendLoading(false)
  }, [loginCredentials, t, resendCooldown])

  const getResendButtonText = () => {
    if (resendCooldown > 0) return t('resendCooldown').replace('{seconds}', resendCooldown.toString())
    return t('resendBtn')
  }

  return (
    <>
      <Wrapper className='w-1/3 mx-auto' style={{ paddingBottom: '5vh', textAlign: 'center' }}>
        <Typography text={t('title')} type="title" center style={undefined} size={23} />
        <Separator size={hp(2)} />
        <div style={{ textAlign: 'center' }}>
          <Typography
            text={t('subtitle1')}
            center
            style={{ display: 'inline' }}
            size={17}
          />
          <Typography
            text={partialTarget}
            bold
            center
            style={{ display: 'inline' }}
            size={17}
          />
          <Typography
            text={t('subtitle2')}
            center
            style={{ display: 'inline' }}
            size={17}
          />
        </div>
        <Separator size={hp(5)} />

        <OtpContainer onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <OtpInput
              key={i}
              ref={el => { inputRefs.current[i] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              hasValue={!!digit}
              hasError={!!propsError}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              autoFocus={i === 0}
            />
          ))}
        </OtpContainer>

        <Separator size={hp(3)} />
        {propsError && (
          <>
            <Typography text={propsError} center error data-cy="error2FA" style={undefined} size={undefined} />
            <Separator size={hp(3)} />
          </>
        )}

        {loginCredentials && (
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <Typography
              text={t('didntReceive')}
              style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}
              size={undefined}
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
              <DefaultBtn
                label={resendLoading ? t('sending') : getResendButtonText()}
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || resendLoading}
                style={{
                  minWidth: '110px',
                  width: 'auto',
                  height: '28px',
                  backgroundColor: resendCooldown > 0 || resendLoading ? 'rgba(204, 204, 204, 0.7)' : 'rgba(28, 22, 91, 0.15)',
                  borderRadius: '80px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
                  color: '#fff',
                  fontWeight: 400,
                  fontSize: '12px',
                  cursor: resendCooldown > 0 || resendLoading ? 'not-allowed' : 'pointer',
                  padding: '0 12px',
                }}
                nextIcon=""
              />
            </div>
            {resendMessage && (
              <Typography
                text={resendMessage}
                center
                style={{
                  color: resendMessage === t('resendSuccess') ? '#4CAF50' : '#f44336',
                  fontSize: '12px',
                  fontWeight: 'normal',
                  marginTop: '8px',
                }}
                size={undefined}
              />
            )}
          </div>
        )}

        <Separator size={hp(4)} />

        <DefaultBtn
          label={t('btnLabel')}
          withNext
          reversedColors
          disabled={code.length !== OTP_LENGTH || loading}
          onClick={handleSubmit}
          style={{ margin: 'auto' }}
          nextIcon={undefined}
        />
      </Wrapper>
    </>
  )
}

CodeForm.whyDidYouRender = true

export default memo(CodeForm)
